/**
 * Component Factory
 * Automatically initializes components based on data attributes or selectors
 */

import { querySelector, querySelectorAll } from './dom.js';

/**
 * Component registry to store component constructors
 */
const componentRegistry = new Map();

/**
 * Register a component class
 * @param {string} name - Component name
 * @param {Function} ComponentClass - Component constructor
 */
export function registerComponent(name, ComponentClass) {
  componentRegistry.set(name, ComponentClass);
}

/**
 * Initialize a single component
 * @param {string} selector - CSS selector for the component
 * @param {Function} ComponentClass - Component constructor
 * @param {Object} options - Component options
 * @returns {Object|null} - Component instance or null
 */
export function initComponent(selector, ComponentClass, options = {}) {
  const element = querySelector(selector);

  if (!element) {
    console.warn(`Component element not found: ${selector}`);
    return null;
  }

  try {
    return new ComponentClass(selector, options);
  } catch (error) {
    console.error(`Error initializing component ${selector}:`, error);
    return null;
  }
}

/**
 * Initialize multiple instances of a component
 * @param {string} selector - CSS selector for the components
 * @param {Function} ComponentClass - Component constructor
 * @param {Object} options - Component options
 * @returns {Array} - Array of component instances
 */
export function initComponents(selector, ComponentClass, options = {}) {
  const elements = querySelectorAll(selector);
  const instances = [];

  elements.forEach((element, index) => {
    try {
      const instance = new ComponentClass(element, options);
      instances.push(instance);
    } catch (error) {
      console.error(`Error initializing component ${selector}[${index}]:`, error);
    }
  });

  return instances;
}

/**
 * Initialize all registered components automatically
 * Looks for elements with data-component attribute
 * @returns {Map} - Map of component instances by name
 */
export function initAllComponents() {
  const instances = new Map();

  componentRegistry.forEach((ComponentClass, name) => {
    const selector = `[data-component="${name}"]`;
    const elements = querySelectorAll(selector);

    elements.forEach((element, index) => {
      try {
        const instanceKey = `${name}-${index}`;
        const instance = new ComponentClass(element);
        instances.set(instanceKey, instance);
      } catch (error) {
        console.error(`Error initializing component ${name}[${index}]:`, error);
      }
    });
  });

  return instances;
}

/**
 * Destroy a component instance
 * @param {Object} component - Component instance
 */
export function destroyComponent(component) {
  if (component && typeof component.destroy === 'function') {
    try {
      component.destroy();
    } catch (error) {
      console.error('Error destroying component:', error);
    }
  }
}
